import React, { useEffect, useRef } from "react";

import "./ArticleContentViewer.css";

const computeScrollMetrics = (element) => {
    if (!element) {
        return {
            scrollPercent: 0,
            estimatedReadPercent: 0,
            lastScrollTop: 0,
        };
    }

    const maxScrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
    const scrollTop = Math.max(0, element.scrollTop || 0);
    const scrollPercent = maxScrollTop > 0 ? Math.round((scrollTop / maxScrollTop) * 100) : 100;

    return {
        scrollPercent,
        estimatedReadPercent: scrollPercent,
        lastScrollTop: Math.round(scrollTop),
    };
};

export default function ArticleContentViewer({ content, progress, onTrack, onContextChange, navigationRequest }) {
    const articleRef = useRef(null);
    const savedScrollTopRef = useRef(0);
    const handledNavigationRef = useRef(null);

    useEffect(() => {
        savedScrollTopRef.current = Number(progress?.progressPayload?.lastScrollTop || 0);
    }, [content?.id, progress?.progressPayload?.lastScrollTop]);

    useEffect(() => {
        const articleElement = articleRef.current;
        if (!articleElement) return undefined;

        const savedScrollTop = savedScrollTopRef.current;
        if (savedScrollTop > 0) {
            window.requestAnimationFrame(() => {
                articleElement.scrollTop = savedScrollTop;
            });
        }

        const emitMetrics = (timeSpentDeltaSeconds = 0, immediate = false) => {
            const metrics = computeScrollMetrics(articleElement);
            onContextChange?.({
                contentTypeContext: "ARTICLE",
                scrollPosition: metrics.lastScrollTop,
            });
            onTrack?.({
                timeSpentDeltaSeconds,
                completionPercent: Math.max(metrics.scrollPercent, metrics.estimatedReadPercent),
                progressPayload: metrics,
                immediate,
            });
        };

        const timerId = window.setInterval(() => {
            if (!document.hidden) {
                emitMetrics(1, false);
            }
        }, 1000);

        const handleScroll = () => emitMetrics(0, false);
        articleElement.addEventListener("scroll", handleScroll);

        return () => {
            window.clearInterval(timerId);
            articleElement.removeEventListener("scroll", handleScroll);
        };
    }, [content?.id, onContextChange, onTrack]);

    useEffect(() => {
        const articleElement = articleRef.current;
        const requestId = navigationRequest?.requestId;
        const scrollPosition = navigationRequest?.scrollPosition;
        if (!articleElement || !requestId || scrollPosition == null || handledNavigationRef.current === requestId) return;
        handledNavigationRef.current = requestId;
        articleElement.scrollTop = Math.max(0, Number(scrollPosition || 0));
    }, [navigationRequest]);

    return (
        <div
            ref={articleRef}
            className="acv-scroll"
            dangerouslySetInnerHTML={{ __html: content.articleContent || "<p>Aucun contenu d'article disponible.</p>" }}
        />
    );
}
